import { z } from 'zod';
import { emailLower, phoneInput } from './common.js';
import { qualificationStatus } from './enums.js';
import { leadClient, leadSource } from './lead.js';
import { failedCriterion } from './qualification.js';
import { genericAnswers } from './qualification-submission.js';

// =============================================================================
// Public API wire format: POST /public/submit-qualification
// The backend resolves brokerSubdomain → brokerId, normalizes the phone to
// E.164, hashes PII for CAPI, runs qualification (delegated to the vertical's
// qualifier), persists the lead + submission, fires CAPI events, and returns
// the result below.
//
// `answers` is intentionally loose: the API resolves the broker's vertical and
// re-validates `answers` against that vertical's FormConfig schema. Forcing a
// rigid MCMV shape here would block any other vertical from posting through
// the same endpoint.
// =============================================================================

export const submitQualificationInput = z
  .object({
    brokerSubdomain: z.string().trim().min(2).max(30),
    propertySlug: z.string().trim().min(2).max(80).optional(),
    name: z.string().trim().min(2).max(160),
    email: emailLower,
    phone: phoneInput,
    answers: genericAnswers,
    source: leadSource.optional(),
    client: leadClient.optional(),
    /**
     * Honeypot · hidden field that humans don't see and don't fill.
     * Any non-empty value triggers a silent no-op response on the server.
     * Accept any string here so the schema doesn't surface a 400 (which would
     * tell the bot that this is a guarded field).
     */
    _honeypot: z.string().optional(),
  })
  .strict();

export type SubmitQualificationInput = z.infer<typeof submitQualificationInput>;

export const submitQualificationResponse = z
  .object({
    leadId: z.string(),
    qualificationStatus: qualificationStatus,
    /**
     * Vertical key da submissão. Permite o FE escolher a variante certa
     * da tela de resultado · MCMV mostra score+critérios; PV mostra
     * "cotação enviada → WhatsApp" sem score.
     */
    verticalKey: z.string().min(2).max(40),
    /** UUID v4 · pass this to the browser pixel as `eventID` to dedup with the server-side event. */
    metaEventId: z.string().uuid(),
    /** wa.me URL pre-filled with the message template for this status. */
    whatsappUrl: z.string().url(),
    /** Criteria the applicant failed · surfaced in the result page transparency card. */
    failedCriteria: z.array(failedCriterion),
    /** 0–100, used by the result page to show a visual confidence bar. */
    score: z.number().int().min(0).max(100),
  })
  .strict();

export type SubmitQualificationResponse = z.infer<typeof submitQualificationResponse>;
